import { useState } from "react";
import { resizedUrl, noteResizeFailure } from "../lib/img";

/**
 * Smart image wrapper that:
 *   - Lazy-loads off-screen images (native browser API)
 *   - Async-decodes so the main thread isn't blocked
 *   - Renders a subtle shimmer skeleton until the image is ready
 *   - Fades the image in once loaded (no jarring pop)
 *
 * Use this anywhere we display user-uploaded covers / thumbnails / avatars
 * coming from Emergent Object Storage (or any slowish CDN).
 *
 * Props mirror native <img> plus an optional `className` for the wrapper.
 * `width` (px, ideally 2x the displayed size) loads a small resized copy when
 * one is available, falling back to the original if it fails; `still` makes
 * animated images (GIFs) a single frame.
 */
export default function LazyImage({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  eager = false,
  fallback = null,
  width,
  still = false,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [useOriginal, setUseOriginal] = useState(false);
  const shown = width && !useOriginal ? resizedUrl(src, width, { still }) : src;

  if (!src || errored) {
    return (
      <div className={`relative overflow-hidden bg-[#1A1A1A] ${className}`}>
        {fallback}
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden bg-[#1A1A1A] ${className}`}>
      {!loaded && (
        <div
          aria-hidden
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#1A1A1A] via-[#232323] to-[#1A1A1A]"
        />
      )}
      <img
        src={shown}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (shown !== src) {
            noteResizeFailure();
            setUseOriginal(true); // the resized copy failed: try the original
          } else {
            setErrored(true);
          }
        }}
        className={`${imgClassName} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        {...rest}
      />
    </div>
  );
}
